import { apiClient } from 'axios-client';
import { LastCheckedProReview } from 'components/last-checked-pro-review';
import { startOfMonth, subMonths } from 'date-fns';
import { useFormikContext } from 'formik';
import { useProInfosQueryLazyQuery } from 'generated/graphql';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Divider,
  Icon,
  Label as LabelUi,
  List,
  Loader,
  Message,
  Popup,
} from 'semantic-ui-react';
import { formatDate, PhoneNumber } from 'utils';
import { ProFormUpdateFields } from '../pro-form-update-fields-type';

type ProInfosProps = {
  proViewId?: number;
};

const ProInfos: FunctionComponent<ProInfosProps> = ({ proViewId }) => {
  const { values } = useFormikContext<ProFormUpdateFields>();
  const [proInfosQuery, proInfosQueryResult] = useProInfosQueryLazyQuery();
  const [lastBillingAmount, setLastBillingAmount] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    if (proViewId) {
      proInfosQuery({ variables: { proViewId } });
      apiClient
        .post('event/stats', {
          after: startOfMonth(subMonths(new Date(), 1)),
          before: startOfMonth(new Date()),
          proViewId,
        })
        .then((response) => {
          // TODO Link manager to amount instead of get globalAmount
          setLastBillingAmount(response.data?.[0]?.billing?.totalAmount);
        });
    }
  }, [proViewId]);

  const proView = proInfosQueryResult.data?.proViewByPk;
  const proPresentation = proView?.proPresentation;

  const webPhoneNumber = values.phoneNumbers.find(({ tag }) => tag === 'web');
  const guidePhoneNumber = values.phoneNumbers.find(
    ({ tag }) => tag === 'guide',
  );
  const releasedPhoneNumbers = values.phoneNumbers.filter(
    ({ tag }) => tag === 'released',
  );
  const otherPhoneNumbers = values.phoneNumbers.filter(
    ({ tag }) => tag !== 'web' && tag !== 'guide' && tag !== 'released',
  );

  return (
    <List verticalAlign="middle">
      <List.Item>
        <List.Content floated="right" style={{ textAlign: 'right' }}>
          {proPresentation?.averageRating ? (
            <LabelUi color="yellow">
              <Icon name="star" />
              {proPresentation.averageRating.toFixed(1).replace('.', ',')}
            </LabelUi>
          ) : (
            "Pas d'avis validés"
          )}
        </List.Content>
        <List.Content>Note moyenne</List.Content>
      </List.Item>
      <List.Item>
        <List.Content floated="right">
          {proPresentation ? (
            <>
              <Popup
                trigger={
                  <LabelUi
                    as={Link}
                    to={`/reviews?presentationId=${proPresentation.id}&status=ok`}
                    color="green"
                  >
                    {proPresentation?.publishedReviewCount}
                  </LabelUi>
                }
                content="Publiés"
                position="top center"
              />
              <Popup
                trigger={
                  <LabelUi
                    as={Link}
                    color="red"
                    to={`/reviews?presentationId=${proPresentation.id}&status=todo`}
                  >
                    {proPresentation?.pendingReviewCount}
                  </LabelUi>
                }
                position="top center"
                content="En attente"
              />
            </>
          ) : (
            <Loader active inline size="tiny" />
          )}
        </List.Content>
        <List.Content>Avis</List.Content>
      </List.Item>
      <Divider hidden />
      {webPhoneNumber ? (
        <List.Item>
          <List.Content floated="right">
            <LabelUi color="grey">
              {PhoneNumber.format(webPhoneNumber.number)}
            </LabelUi>
          </List.Content>
          <List.Content>Numéro web</List.Content>
        </List.Item>
      ) : null}

      {guidePhoneNumber ? (
        <List.Item>
          <List.Content floated="right">
            <LabelUi color="grey">
              {PhoneNumber.format(guidePhoneNumber.number)}
            </LabelUi>
          </List.Content>
          <List.Content>Numéro guide</List.Content>
        </List.Item>
      ) : null}

      {releasedPhoneNumbers.length ? (
        <List.Item>
          <List.Content floated="right">
            {releasedPhoneNumbers.map(({ number }) => (
              <div key={number} style={{ marginBottom: '5px' }}>
                <LabelUi color="grey" key={number}>
                  {PhoneNumber.format(number)}
                </LabelUi>
              </div>
            ))}
          </List.Content>
          <List.Content>Numéros supprimés</List.Content>
        </List.Item>
      ) : null}

      {otherPhoneNumbers.length ? (
        <List.Item>
          <List.Content floated="right">
            {otherPhoneNumbers.map(({ number, tag }) => (
              <div key={number} style={{ marginBottom: '5px' }}>
                <Popup
                  content={tag}
                  trigger={
                    <LabelUi color="grey">{PhoneNumber.format(number)}</LabelUi>
                  }
                />
              </div>
            ))}
          </List.Content>
          <List.Content>Autre(s)</List.Content>
        </List.Item>
      ) : null}

      <Divider hidden />
      {proViewId ? (
        <>
          <List.Item>
            <List.Content floated="right">
              {proView ? (
                <LabelUi color="grey">
                  {formatDate(proView.dateCreated)}
                </LabelUi>
              ) : (
                <Loader active inline size="tiny" />
              )}
            </List.Content>
            <List.Content>Créé le</List.Content>
          </List.Item>

          {proView?.datePublished && (
            <List.Item>
              <List.Content floated="right">
                <LabelUi color="grey">
                  {formatDate(proView?.datePublished)}
                </LabelUi>
              </List.Content>
              <List.Content>Publié le</List.Content>
            </List.Item>
          )}
          {proPresentation?.company?.firstDatePublished?.date && (
            <List.Item>
              <List.Content floated="right">
                <LabelUi color="grey">
                  {formatDate(
                    proPresentation.company?.firstDatePublished?.date,
                  )}
                </LabelUi>
              </List.Content>
              <List.Content>Recommandé depuis</List.Content>
            </List.Item>
          )}
          <List.Item>
            <List.Content floated="right">
              {proPresentation ? (
                <LabelUi color="grey">
                  <LastCheckedProReview
                    proPresentationId={proPresentation?.id}
                  />
                </LabelUi>
              ) : (
                <Loader active inline size="tiny" />
              )}
            </List.Content>
            <List.Content>Dernier avis vérifié</List.Content>
          </List.Item>
          <List.Item>
            <List.Content floated="right">
              <LabelUi color="grey">
                {lastBillingAmount ? `${lastBillingAmount}€` : 'Aucune'}
              </LabelUi>
            </List.Content>
            <List.Content>Facturation M-1</List.Content>
          </List.Item>
          {proView?.status !== 'archived' && proView?.dateArchiveScheduled ? (
            <List.Item>
              <List.Content floated="right">
                <LabelUi
                  color="red"
                  floated="right"
                  style={{ textAlign: 'right', marginBottom: '15px' }}
                >
                  {formatDate(proView.dateArchiveScheduled)}
                </LabelUi>
              </List.Content>
              <List.Content>Archivage programmé</List.Content>
            </List.Item>
          ) : null}
          {proView?.dateArchived && (
            <List.Item>
              <List.Content floated="right">
                {proView ? (
                  <LabelUi color="grey">
                    {formatDate(new Date(proView.dateArchived).toISOString())}
                  </LabelUi>
                ) : (
                  <Loader active inline size="tiny" />
                )}
              </List.Content>
              <List.Content>Archivé le</List.Content>
            </List.Item>
          )}
        </>
      ) : null}
      {proView?.archiveReason && (
        <>
          <Divider horizontal>Raison de l&apos;archivage</Divider>
          <Message color="grey">{proView.archiveReason}</Message>
        </>
      )}
    </List>
  );
};

export default ProInfos;
